import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  TreePine,
  Sun,
  Leaf,
  Heart,
  Calendar,
  Mail,
  Phone,
  MapPin,
  ChevronDown,
  Menu,
  X,
  CloudSun,
  Sparkles,
  Users,
  Clock,
  Shield,
  Image as ImageIcon,
  Shirt,
  Backpack,
  Snowflake,
  Droplets,
  Info,
  Car,
  Baby,
  Bug,
  Lightbulb,
  Apple } from
"lucide-react";

// Leer lassen, wenn Frontend und API auf derselben Domain laufen (Railway/Docker).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const FALLBACK_ERROR_MESSAGE =
  "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.";

// FastAPI liefert bei Validierungsfehlern eine Liste statt eines Textes.
const readErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) {
    return `Bitte prüfen Sie Ihre Eingaben: ${detail[0].msg}`;
  }
  return FALLBACK_ERROR_MESSAGE;
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
  { href: "#home", label: "Home" },
  { href: "#philosophie", label: "Philosophie" },
  { href: "#team", label: "Team" },
  { href: "#angebot", label: "Angebot" },
  { href: "#infos", label: "Infos" },
  { href: "#kleidung", label: "Kleidung" },
  { href: "#galerie", label: "Galerie" },
  { href: "#kontakt", label: "Kontakt" }];


  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? "nav-glass shadow-md py-2" : "bg-transparent py-4"}`
      }
      data-testid="main-navigation">

      <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#home"
          className="flex items-center gap-2 group"
          data-testid="nav-logo">

          <img
            src="https://static.wixstatic.com/media/0c04d6_85ffbb0ed57444348824e28d78023ef4~mv2.gif"
            alt="Waldspielgruppe Sunnebergli Logo"
            className="w-12 h-12 group-hover:scale-110 transition-transform" />

          <span className="font-handwritten text-2xl font-bold text-[#4A6741] hidden sm:block">
            Sunnebergli
          </span>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
          <a
            key={link.href}
            href={link.href}
            className="text-[#2C3E50] hover:text-[#4A6741] font-semibold transition-colors relative group"
            data-testid={`nav-link-${link.label.toLowerCase()}`}>

              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#4A6741] transition-all group-hover:w-full"></span>
            </a>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 text-[#4A6741]"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu">

          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen &&
      <div className="md:hidden absolute top-full left-0 right-0 bg-[#FEFAE0] shadow-lg py-4 mobile-menu-scroll" data-testid="mobile-menu">
          {navLinks.map((link) =>
        <a
          key={link.href}
          href={link.href}
          className="block px-6 py-3 text-[#2C3E50] hover:bg-[#E9EDC9] hover:text-[#4A6741] font-semibold transition-colors"
          onClick={() => setIsMobileMenuOpen(false)}
          data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}>

              {link.label}
            </a>
        )}
        </div>
      }
    </nav>);

};

// Wave Divider SVG Component
const WaveDivider = ({ color = "#FEFAE0", flip = false }) =>
<div className={`wave-divider ${flip ? "rotate-180" : ""}`}>
    <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
      <path
      d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
      fill={color} />

    </svg>
  </div>;


// Hero Section
const HeroSection = () =>
<section
  id="home"
  className="hero-section relative overflow-hidden"
  style={{
    backgroundImage: `url('https://images.unsplash.com/photo-1615239095558-41ac83720423?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920')`
  }}
  data-testid="hero-section">

    <div className="hero-overlay"></div>
    
    {/* Decorative leaves */}
    <div className="leaf-decoration top-20 left-10 text-6xl animate-float">🍃</div>
    <div className="leaf-decoration top-40 right-20 text-4xl animate-float" style={{ animationDelay: "1s" }}>🍂</div>
    
    <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 text-center">
      {/* Sun Logo */}
      <div className="sun-glow mb-6 animate-fade-in-up">
        <img
        src="https://static.wixstatic.com/media/0c04d6_85ffbb0ed57444348824e28d78023ef4~mv2.gif"
        alt="Waldspielgruppe Sunnebergli Logo"
        className="w-32 h-32 md:w-40 md:h-40 float" />

      </div>
      
      {/* Title */}
      <h1
      className="font-handwritten text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-[#4A6741] mb-2 animate-fade-in-up delay-100"
      data-testid="hero-title">

        Waldspielgruppe
      </h1>
      <h2
      className="font-handwritten text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#FFD700] mb-8 animate-fade-in-up delay-200"
      style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.1)" }}
      data-testid="hero-subtitle">

        Sunnebergli
      </h2>
      
      {/* Motto */}
      <p
      className="text-xl md:text-2xl text-[#2C3E50] font-semibold italic mb-8 animate-fade-in-up delay-300"
      data-testid="hero-motto">

        "Hilf mir es selbst zu tun!"
      </p>
      
      {/* Location */}
      <div className="flex items-center gap-2 text-[#4A6741] animate-fade-in-up delay-400">
        <MapPin className="w-5 h-5" />
        <span className="font-semibold">Elsau, Schweiz</span>
      </div>
      
      {/* CTA Button */}
      <a
      href="#kontakt"
      className="mt-8 px-8 py-4 bg-[#4A6741] text-white font-bold text-lg btn-organic hover:bg-[#3A5230] shadow-lg animate-fade-in-up delay-500"
      data-testid="hero-cta">

        Jetzt Anmelden
      </a>
      
      {/* Scroll Indicator */}
      <div className="scroll-indicator text-[#4A6741]">
        <ChevronDown className="w-8 h-8" />
      </div>
    </div>
    
    <WaveDivider color="#FEFAE0" />
  </section>;


// Philosophy Section
const PhilosophieSection = () => {
  const features = [
  {
    icon: <TreePine className="w-8 h-8" />,
    title: "Natur erleben",
    description: "Die Natur hautnah erleben und spüren. Alle Sinne werden geweckt."
  },
  {
    icon: <Sparkles className="w-8 h-8" />,
    title: "Kreativ gestalten",
    description: "Mit natürlichen Materialien kreativ gestalten und der Fantasie freien Lauf lassen."
  },
  {
    icon: <Users className="w-8 h-8" />,
    title: "Soziale Kontakte",
    description: "Freundschaften knüpfen und lernen, Rücksicht auf andere zu nehmen."
  },
  {
    icon: <CloudSun className="w-8 h-8" />,
    title: "Vier Jahreszeiten",
    description: "Die vier Jahreszeiten intensiv erleben und Neues entdecken."
  }];


  return (
    <section
      id="philosophie"
      className="py-20 px-4 section-cream"
      data-testid="philosophie-section">

      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-[#D4A373] mb-4">
            <Leaf className="w-5 h-5" />
            <span className="font-semibold uppercase tracking-wider text-sm">Unsere Philosophie</span>
          </div>
          <h2
            className="font-handwritten text-4xl md:text-5xl lg:text-6xl font-bold text-[#4A6741] mb-6"
            data-testid="philosophie-title">

            Hilf mir es selbst zu tun!
          </h2>
          <p className="text-lg text-[#2C3E50] max-w-3xl mx-auto leading-relaxed">
            Was gibt es Schöneres, als die Natur hautnah zu erleben und zu spüren. 
            In der Waldspielgruppe lernt Ihr Kind spielend sich und seine Umwelt kennen. 
            Es wird geforscht, entdeckt und experimentiert... so werden alle Sinne geweckt.
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) =>
          <div
            key={index}
            className={`bg-white p-6 rounded-2xl soft-shadow ${
            index % 2 === 0 ? "card-tilted" : "card-tilted-left"} border-2 border-[#E9EDC9]`
            }
            data-testid={`feature-card-${index}`}>

              <div className="icon-blob mb-4 text-[#4A6741]">
                {feature.icon}
              </div>
              <h3 className="font-handwritten text-2xl font-bold text-[#4A6741] mb-2">
                {feature.title}
              </h3>
              <p className="text-[#2C3E50] text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          )}
        </div>

        {/* Additional Text */}
        <div className="mt-16 bg-[#FAEDCD] p-8 md:p-12 rounded-3xl soft-shadow">
          <div className="max-w-3xl mx-auto text-center">
            <Heart className="w-10 h-10 text-[#D4A373] mx-auto mb-4" />
            <p className="text-lg text-[#2C3E50] leading-relaxed mb-4">
              An der frischen Luft können die Kinder sich austoben, frei spielen, 
              eine Geschichte hören, werken und soziale Kontakte knüpfen. Sie lernen aber auch, 
              Rücksicht auf andere Kinder und die Natur zu nehmen. 
              <strong> Diese natürlichen Erfahrungen prägen Ihr Kind.</strong>
            </p>
            <p className="text-lg text-[#4A6741] font-semibold italic">
              Wir freuen uns, dies alles zusammen mit Ihrem Kind erleben zu dürfen.
            </p>
          </div>
        </div>
      </div>
    </section>);

};

// Team Section
const TeamSection = () => {
  return (
    <section
      id="team"
      className="py-20 px-4 section-surface"
      data-testid="team-section">

      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-[#D4A373] mb-4">
            <Users className="w-5 h-5" />
            <span className="font-semibold uppercase tracking-wider text-sm">Unser Team</span>
          </div>
          <h2
            className="font-handwritten text-4xl md:text-5xl lg:text-6xl font-bold text-[#4A6741]"
            data-testid="team-title">

            Wer sind wir?
          </h2>
        </div>

        {/* Team Members Grid - Side by Side */}
        <div className="grid md:grid-cols-2 gap-8">
          
          {/* Barbara Berger */}
          <div
            className="bg-white rounded-3xl soft-shadow overflow-hidden border-2 border-[#E9EDC9]"
            data-testid="team-member-barbara">

            {/* Photo */}
            <div className="bg-[#E9EDC9] flex items-center justify-center p-8">
              <img
                src="https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/qok39mq6_WhatsApp%20Image%202026-03-02%20at%2008.01.33.jpeg"
                alt="Barbara Berger"
                className="w-40 h-40 rounded-full object-cover border-4 border-white shadow-lg" />

            </div>
            
            {/* Content */}
            <div className="p-6 md:p-8">
              <h3 className="font-handwritten text-3xl font-bold text-[#4A6741] mb-1 text-center">
                Barbara Berger
              </h3>
              <p className="text-[#D4A373] font-semibold mb-5 text-center">Spielgruppenleiterin</p>
              
              {/* Qualifications */}
              <div className="mb-5 space-y-2">
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#E9EDC9] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#4A6741] text-xs">✓</span>
                  </div>
                  <p className="text-[#2C3E50] text-sm">
                    Zertifizierte Spielgruppenleiterin IG Spielgruppen Bildung GmbH mit Weiterbildung Wald
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#E9EDC9] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#4A6741] text-xs">✓</span>
                  </div>
                  <p className="text-[#2C3E50] text-sm">
                    Absolvierter Kleinkinder-Nothelferkurs
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#E9EDC9] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#4A6741] text-xs">✓</span>
                  </div>
                  <p className="text-[#2C3E50] text-sm">
                    Mutter von zwei wundervollen Töchtern (1997 und 2001)
                  </p>
                </div>
              </div>
              
              {/* Personal Text */}
              <div className="bg-[#FEFAE0] p-4 rounded-xl">
                <p className="text-[#2C3E50] text-sm leading-relaxed mb-3">Ich wohne in Rickenbach ZH. Nebst der Waldspielgruppe biete ich auch Reiki-Sessions in Winterthur an. (

                  <a href="https://seelenlichtweg.ch" target="_blank" rel="noopener noreferrer" className="text-[#4A6741] hover:text-[#D4A373] underline font-semibold">Seelenlichtweg.ch</a>)
                </p>
                <p className="text-[#2C3E50] text-sm leading-relaxed mb-3">
                  Die Natur in allen Facetten zu erleben, hat mir schon immer gut getan. Da kann ich super Energie tanken.
                </p>
                <p className="text-[#2C3E50] text-sm leading-relaxed">
                  <Heart className="w-4 h-4 text-[#D4A373] inline mr-1" />
                  <span className="italic">
                    Es ist mir eine Herzenssache, auch Ihrem Kind die Vielfalt der Natur näherzubringen. 
                    Im Wald gibt es so viel zu entdecken, ganz ohne Spielsachen. Das erweckt alle Sinne 
                    und ist so wichtig für die Entwicklung jedes Kindes.
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Nathalie Schmid */}
          <div
            className="bg-white rounded-3xl soft-shadow overflow-hidden border-2 border-[#E9EDC9]"
            data-testid="team-member-nathalie">

            {/* Photo */}
            <div className="bg-[#E9EDC9] flex items-center justify-center p-8">
              <img
                src="https://static.wixstatic.com/media/0c04d6_54afe72550f54e529add17a965882e58~mv2.jpeg/v1/crop/x_0,y_303,w_640,h_644/fill/w_216,h_217,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202019-01-06%20at%2010_27_33%20PM.jpeg"
                alt="Nathalie Schmid"
                className="w-40 h-40 rounded-full object-cover border-4 border-white shadow-lg" />

            </div>
            
            {/* Content */}
            <div className="p-6 md:p-8">
              <h3 className="font-handwritten text-3xl font-bold text-[#4A6741] mb-1 text-center">
                Nathalie Schmid
              </h3>
              <p className="text-[#D4A373] font-semibold mb-5 text-center">Spielgruppenleiterin</p>
              
              {/* Qualifications */}
              <div className="mb-5 space-y-2">
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#E9EDC9] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#4A6741] text-xs">✓</span>
                  </div>
                  <p className="text-[#2C3E50] text-sm">
                    Zertifizierte Spielgruppenleiterin IG Spielgruppen Bildung GmbH mit Weiterbildung Wald
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#E9EDC9] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#4A6741] text-xs">✓</span>
                  </div>
                  <p className="text-[#2C3E50] text-sm">
                    Absolvierter Kleinkinder-Nothelferkurs
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#E9EDC9] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#4A6741] text-xs">✓</span>
                  </div>
                  <p className="text-[#2C3E50] text-sm">
                    Mutter von 3 Kindern (2000 / 2002 / 2007)
                  </p>
                </div>
              </div>
              
              {/* Personal Text */}
              <div className="bg-[#FEFAE0] p-4 rounded-xl">
                <p className="text-[#2C3E50] text-sm leading-relaxed mb-3">
                  Mit meinen Kindern bin ich oft und gerne in den Wald oder einfach an die frische Luft gegangen, 
                  um zu spielen, etwas zu erkunden oder auch mal ein Feuer zu machen.
                </p>
                <p className="text-[#2C3E50] text-sm leading-relaxed mb-3">
                  Ich leite seit 2009 das Kinderturnen in Elsau und arbeite seit April 2025 noch<br />2-3 Tage pro Woche in einer Kita.
                </p>
                <p className="text-[#2C3E50] text-sm leading-relaxed">
                  <Heart className="w-4 h-4 text-[#D4A373] inline mr-1" />
                  <span className="italic">
                    Das Arbeiten mit den Kindern erfüllt mich mit viel Freude.
                  </span>
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>);

};

// Angebot Section
const AngebotSection = () => {
  const quartale = [
  { num: 1, months: "August - Oktober", season: "Herbst" },
  { num: 2, months: "November - Januar", season: "Winter" },
  { num: 3, months: "Februar - April", season: "Frühling" },
  { num: 4, months: "Mai - Juli", season: "Sommer" }];


  const infos = [
  {
    icon: <Calendar className="w-6 h-6" />,
    title: "Abrechnung",
    content: "Wir rechnen pro Quartal ab. Ein Quartal kostet 305.- CHF. Für eine definitive Anmeldung bezahlen Sie 100.- CHF im Voraus. Den restlichen Betrag können Sie bis 1 Monat vor Quartalsbeginn bezahlen. Im Betrag sind Material, Vorbereitung und Lohn enthalten."
  },
  {
    icon: <Shield className="w-6 h-6" />,
    title: "Versicherung",
    content: "Die Kinder sind nicht durch die Waldspielgruppe versichert. Das ist Sache der Eltern."
  },
  {
    icon: <Clock className="w-6 h-6" />,
    title: "Ferien & Wetter",
    content: "Die Ferien richten sich nach dem Schulferienplan der Gemeinde Elsau. Wir sind grundsätzlich bei jedem Wetter im Wald. Bei Sturm oder Gewitter haben wir die Möglichkeit, in einen trockenen und sicheren Raum zu gehen."
  }];


  return (
    <section
      id="angebot"
      className="py-20 px-4 section-leaf relative"
      data-testid="angebot-section">

      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-[#D4A373] mb-4">
            <Calendar className="w-5 h-5" />
            <span className="font-semibold uppercase tracking-wider text-sm">Unser Angebot</span>
          </div>
          <h2
            className="font-handwritten text-4xl md:text-5xl lg:text-6xl font-bold text-[#4A6741]"
            data-testid="angebot-title">

            Quartale & Informationen
          </h2>
        </div>

        {/* Quartale Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {quartale.map((q) =>
          <div
            key={q.num}
            className="quartal-card"
            data-testid={`quartal-${q.num}`}>

              <div className="text-3xl font-handwritten font-bold text-[#FFD700] mb-1">
                {q.num}. Quartal
              </div>
              <div className="text-[#4A6741] font-semibold">{q.months}</div>
              <div className="text-sm text-[#D4A373] mt-1">{q.season}</div>
            </div>
          )}
        </div>

        {/* Info Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {infos.map((info, index) =>
          <div
            key={index}
            className="bg-white p-6 rounded-2xl soft-shadow border-l-4 border-[#4A6741]"
            data-testid={`info-card-${index}`}>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-[#E9EDC9] flex items-center justify-center text-[#4A6741]">
                  {info.icon}
                </div>
                <h3 className="font-handwritten text-2xl font-bold text-[#4A6741]">
                  {info.title}
                </h3>
              </div>
              <p className="text-[#2C3E50] text-sm leading-relaxed">
                {info.content}
              </p>
            </div>
          )}
        </div>

        {/* Austritt Info */}
        <div className="mt-8 bg-[#FEFAE0] p-6 rounded-2xl soft-shadow">
          <h3 className="font-handwritten text-2xl font-bold text-[#4A6741] mb-3 flex items-center gap-2">
            <Clock className="w-6 h-6" />
            Probezeit & Austritt
          </h3>
          <ul className="text-[#2C3E50] space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-[#4A6741]">•</span>
              Der erste Monat gilt als Probezeit. Bis dahin kann kurzfristig gekündigt werden.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#4A6741]">•</span>
              Nach der Probezeit kann 30 Tage im Voraus auf Ende des Quartals gekündigt werden.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#4A6741]">•</span>
              Bei einem frühzeitigen Austritt wird keine Rückerstattung gewährt.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#4A6741]">•</span>
              Der reservierte Platz wird auch bei Krankheit, Ferien oder anderen Absenzen verrechnet.
            </li>
          </ul>
        </div>
      </div>
    </section>);

};

// Infos Section
const InfosSection = () => {
  return (
    <section
      id="infos"
      className="py-20 px-4 section-cream"
      data-testid="infos-section">

      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-[#D4A373] mb-4">
            <Info className="w-5 h-5" />
            <span className="font-semibold uppercase tracking-wider text-sm">Praktische Infos</span>
          </div>
          <h2
            className="font-handwritten text-4xl md:text-5xl lg:text-6xl font-bold text-[#4A6741]"
            data-testid="infos-title">

            Infos Spielgruppe
          </h2>
        </div>

        {/* Main Info Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          
          {/* Alter */}
          <div className="bg-white p-6 rounded-2xl soft-shadow border-l-4 border-[#FFD700]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#FFF9E6] flex items-center justify-center">
                <Baby className="w-5 h-5 text-[#D4A373]" />
              </div>
              <h3 className="font-handwritten text-xl font-bold text-[#4A6741]">Alter</h3>
            </div>
            <p className="text-[#2C3E50] text-sm">
              Für Kinder um den 3. Geburtstag bis zum Eintritt in den Kindergarten.
            </p>
          </div>

          {/* Wann und Wo */}
          <div className="bg-white p-6 rounded-2xl soft-shadow border-l-4 border-[#4A6741]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#E9EDC9] flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#4A6741]" />
              </div>
              <h3 className="font-handwritten text-xl font-bold text-[#4A6741]">Wann</h3>
            </div>
            <div className="text-[#2C3E50] text-sm space-y-1">
              <p><strong>Montag:</strong> 08:30 - 11:30 Uhr</p>
              <p className="text-[#D4A373] italic">und / oder</p>
              <p><strong>Donnerstag:</strong> 08:30 - 11:30 Uhr</p>
            </div>
          </div>

          {/* Treffpunkt */}
          <div className="bg-white p-6 rounded-2xl soft-shadow border-l-4 border-[#D4A373]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#FAEDCD] flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#D4A373]" />
              </div>
              <h3 className="font-handwritten text-xl font-bold text-[#4A6741]">Treffpunkt</h3>
            </div>
            <div className="text-[#2C3E50] text-sm">
              <p className="font-semibold"></p>
              <p>Sunnenberg 1</p>
              <p>8352 Elsau</p>
            </div>
          </div>

          {/* Parkplätze */}
          <div className="bg-white p-6 rounded-2xl soft-shadow border-l-4 border-[#CCD5AE]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#E9EDC9] flex items-center justify-center">
                <Car className="w-5 h-5 text-[#4A6741]" />
              </div>
              <h3 className="font-handwritten text-xl font-bold text-[#4A6741]">Parkplätze</h3>
            </div>
            <p className="text-[#2C3E50] text-sm">
              Sind vor dem Haus oder an der Strasse vorhanden.
            </p>
          </div>

          {/* Znüni */}
          <div className="bg-white p-6 rounded-2xl soft-shadow border-l-4 border-[#F4A261]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#FFF9E6] flex items-center justify-center">
                <Apple className="w-5 h-5 text-[#F4A261]" />
              </div>
              <h3 className="font-handwritten text-xl font-bold text-[#4A6741]">Znüni</h3>
            </div>
            <p className="text-[#2C3E50] text-sm">
              Die Kinder bringen ihren Znüni und auch das Getränk in einem Rucksack selber mit.
            </p>
          </div>

          {/* Zecken */}
          <div className="bg-white p-6 rounded-2xl soft-shadow border-l-4 border-[#4A6741]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#E9EDC9] flex items-center justify-center">
                <Bug className="w-5 h-5 text-[#4A6741]" />
              </div>
              <h3 className="font-handwritten text-xl font-bold text-[#4A6741]">Zecken</h3>
            </div>
            <p className="text-[#2C3E50] text-sm">
              Vom Frühling bis zum Herbst sind auch die Zecken ein Thema. Schützen Sie Ihr Kind und suchen Sie es zu Hause nach Zecken ab. 
              <a href="http://www.zecken.ch" target="_blank" rel="noopener noreferrer" className="text-[#4A6741] hover:text-[#D4A373] underline ml-1">
                www.zecken.ch
              </a>
            </p>
          </div>
        </div>

        {/* Tipps zur Eingewöhnung */}
        <div className="bg-gradient-to-br from-[#FAEDCD] to-[#E9EDC9] p-8 md:p-10 rounded-3xl soft-shadow">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FFD700] to-[#F4A261] flex items-center justify-center flex-shrink-0">
              <Lightbulb className="w-7 h-7 text-[#4A6741]" />
            </div>
            <div>
              <h3 className="font-handwritten text-2xl font-bold text-[#4A6741] mb-1">
                Gedanken und Tipps
              </h3>
              <p className="text-[#D4A373] text-sm">Zur Eingewöhnung Ihres Kindes</p>
            </div>
          </div>
          
          <div className="space-y-4 text-[#2C3E50]">
            <p className="leading-relaxed">
              <Heart className="w-4 h-4 text-[#D4A373] inline mr-2" />
              Es liegt uns am Herzen, dass sich jedes Kind wohlfühlt bei uns in der Waldspielgruppe! 
              Merken wir, dass ein Kind Mühe hat und die Ablösung evtl. zu früh stattfindet, werden wir Sie informieren.
            </p>
            
            <div className="bg-white/60 p-5 rounded-xl">
              <p className="font-semibold text-[#4A6741] mb-3">So können Sie Ihr Kind vorbereiten:</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFD700]">★</span>
                  Erzählen Sie Ihrem Kind im Vorfeld etwas über die Waldspielgruppe, z.B. vom "Füürlä" oder vom Tierli beobachten.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFD700]">★</span>
                  Informieren Sie Ihr Kind, dass Sie nicht dabei sein werden.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFD700]">★</span>
                  Die Ablösung ist für die Eltern und das Kind nicht einfach. Wenn das Kind aber merkt, dass es für Mami oder Papi okay ist, fällt es ihm viel einfacher loszulassen.
                </li>
              </ul>
            </div>
            
            <p className="text-sm italic text-[#4A6741]">
              Wenn die Eingewöhnung schwierig ist, können wir mit Ihnen das weitere Vorgehen besprechen.
            </p>
          </div>
        </div>
      </div>
    </section>);

};

// Kleidung Section
const KleidungSection = () => {
  return (
    <section
      id="kleidung"
      className="py-20 px-4 section-surface"
      data-testid="kleidung-section">

      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-[#D4A373] mb-4">
            <Shirt className="w-5 h-5" />
            <span className="font-semibold uppercase tracking-wider text-sm">Ausrüstung</span>
          </div>
          <h2
            className="font-handwritten text-4xl md:text-5xl lg:text-6xl font-bold text-[#4A6741]"
            data-testid="kleidung-title">

            Kleidung und Schuhe
          </h2>
          <p className="text-[#2C3E50] mt-4 max-w-2xl mx-auto">
            Voraussetzung für den Spass im Wald ist die richtige Ausrüstung. Die Kleider sollten robust sein, 
            gut abschliessen (Zeckenprävention) und schmutzig werden dürfen.
          </p>
        </div>

        {/* Season Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          
          {/* Frühling/Sommer */}
          <div className="bg-white rounded-3xl soft-shadow overflow-hidden border-2 border-[#E9EDC9]">
            <div className="bg-gradient-to-r from-[#FFD700] to-[#F4A261] p-4">
              <h3 className="font-handwritten text-2xl font-bold text-[#4A6741] flex items-center gap-2">
                <Sun className="w-6 h-6" />
                Frühling - Sommer
              </h3>
            </div>
            <div className="p-6">
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-[#2C3E50]">
                  <span className="w-2 h-2 rounded-full bg-[#4A6741]"></span>
                  Wasserfestes Schuhwerk
                </li>
                <li className="flex items-center gap-3 text-[#2C3E50]">
                  <span className="w-2 h-2 rounded-full bg-[#4A6741]"></span>
                  Bequeme, lange Hosen
                </li>
                <li className="flex items-center gap-3 text-[#2C3E50]">
                  <span className="w-2 h-2 rounded-full bg-[#4A6741]"></span>
                  Langärmliges T-Shirt
                </li>
                <li className="flex items-center gap-3 text-[#2C3E50]">
                  <span className="w-2 h-2 rounded-full bg-[#4A6741]"></span>
                  Pullover oder Faserpelzjacke
                </li>
                <li className="flex items-center gap-3 text-[#2C3E50]">
                  <span className="w-2 h-2 rounded-full bg-[#4A6741]"></span>
                  Evtl. Kopfbedeckung
                </li>
              </ul>
            </div>
          </div>

          {/* Herbst/Winter */}
          <div className="bg-white rounded-3xl soft-shadow overflow-hidden border-2 border-[#E9EDC9]">
            <div className="bg-gradient-to-r from-[#4A6741] to-[#3A5230] p-4">
              <h3 className="font-handwritten text-2xl font-bold text-white flex items-center gap-2">
                <Snowflake className="w-6 h-6" />
                Herbst - Winter - Übergangszeit
              </h3>
            </div>
            <div className="p-6">
              <p className="text-[#D4A373] text-sm italic mb-4">
                "Zwiebellook" - mehrere Kleiderschichten übereinander
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-[#2C3E50]">
                  <span className="w-2 h-2 rounded-full bg-[#4A6741]"></span>
                  Thermounterwäsche
                </li>
                <li className="flex items-center gap-3 text-[#2C3E50]">
                  <span className="w-2 h-2 rounded-full bg-[#4A6741]"></span>
                  Mütze & Fausthandschuhe
                </li>
                <li className="flex items-center gap-3 text-[#2C3E50]">
                  <span className="w-2 h-2 rounded-full bg-[#4A6741]"></span>
                  Wasserfeste, warme Schuhe mit gutem Profil
                </li>
                <li className="flex items-center gap-3 text-[#2C3E50]">
                  <span className="w-2 h-2 rounded-full bg-[#4A6741]"></span>
                  Äusserste Schicht wasserdicht bei Nässe
                </li>
                <li className="flex items-center gap-3 text-[#2C3E50]">
                  <span className="w-2 h-2 rounded-full bg-[#4A6741]"></span>
                  Skisocken (keine Baumwollsocken!)
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Regen Info - Prominent */}
        <div className="bg-gradient-to-r from-[#5B8FB9] to-[#4A7A9E] p-8 rounded-3xl mb-12 soft-shadow">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Droplets className="w-12 h-12 text-white" />
            </div>
            <div className="text-center md:text-left">
              <h4 className="font-handwritten text-3xl font-bold text-white mb-2">Bei Regen</h4>
              <p className="text-white/90 text-lg">
                Wasserdichte <strong>Regenhose</strong> und <strong>Regenjacke</strong>
              </p>
              <p className="text-white/70 text-sm mt-2">
                Wir sind bei fast jedem Wetter draussen - gute Regenkleidung ist ein Muss!
              </p>
            </div>
          </div>
        </div>

        {/* Rucksack Section */}
        <div className="bg-white rounded-3xl soft-shadow p-8 border-2 border-[#E9EDC9]">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FFD700] to-[#F4A261] flex items-center justify-center flex-shrink-0">
              <Backpack className="w-8 h-8 text-[#4A6741]" />
            </div>
            <div>
              <h3 className="font-handwritten text-2xl font-bold text-[#4A6741]">
                Guter Rucksack mit Schnalle vorne
              </h3>
              <p className="text-[#D4A373]">Grösse: ca. 10 Liter</p>
            </div>
          </div>
          
          <h4 className="font-semibold text-[#4A6741] mb-4">Im Rucksack:</h4>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <span className="text-[#FFD700] text-lg">✓</span>
              <p className="text-[#2C3E50] text-sm">
                Ersatzhosen, Unterhosen, Socken und Handschuhe 
                <span className="text-[#D4A373]"> (alles in einem Plastiksack verpackt!)</span>
              </p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FFD700] text-lg">✓</span>
              <p className="text-[#2C3E50] text-sm">
                Getränkeflasche (3-5 dl)
                <span className="text-[#D4A373]"> - im Winter warm</span>
              </p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FFD700] text-lg">✓</span>
              <p className="text-[#2C3E50] text-sm">Znüni</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FFD700] text-lg">✓</span>
              <p className="text-[#2C3E50] text-sm">
                Falls Windeln: Ersatzwindeln und Feuchttücher
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// Gallery Section
const GalerieSection = () => {
  const galleryImages = [
  { id: 1, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/sxpaqtoo_WhatsApp%20Image%202026-03-02%20at%2014.18.43.jpeg", caption: "Abenteuer im Wald" },
  { id: 2, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/yxuyj645_WhatsApp%20Image%202026-03-02%20at%2014.12.51.jpeg", caption: "Natur entdecken" },
  { id: 3, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/pgpfycfg_WhatsApp%20Image%202026-03-02%20at%2014.10.49.jpeg", caption: "Kochen am Feuer" },
  { id: 4, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/8ir7ym5u_WhatsApp%20Image%202026-03-02%20at%2014.06.01.jpeg", caption: "Kreativ im Winter" },
  { id: 5, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/q7xpw36h_IMG-20190528-WA0010.jpg", caption: "Spielen und Balancieren" },
  { id: 6, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/xft1fngq_IMG-20190528-WA0023.jpg", caption: "Würstchen grillieren" },
  { id: 7, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/hwehic4a_20190701_093425.jpg", caption: "Werken mit Holz" },
  { id: 8, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/syeqlfbm_IMG-20190613-WA0004.jpg", caption: "Bei jedem Wetter" },
  { id: 9, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/qb9jlf7b_IMG-20190613-WA0015.jpg", caption: "Gemeinsam bauen" },
  { id: 10, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/4dqq7csh_IMG-20190613-WA0019.jpg", caption: "Zusammen am Tisch" },
  { id: 11, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/clj7ipk7_IMG-20190613-WA0014.jpg", caption: "Entspannung pur" },
  { id: 12, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/ki8ey0fs_20241003_102753.jpg", caption: "Balancieren lernen" },
  { id: 13, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/cy81fjrb_WhatsApp%20Image%202026-03-02%20at%2014.09.40.jpeg", caption: "Winterabenteuer" },
  { id: 14, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/tfdy7ynt_WhatsApp%20Image%202026-03-02%20at%2014.09.34.jpeg", caption: "Znüni-Zeit" },
  { id: 15, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/p74o87bl_WhatsApp%20Image%202026-03-02%20at%2014.22.40%20%282%29.jpeg", caption: "Kreativ am Baum" },
  { id: 16, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/0jaq8c5w_WhatsApp%20Image%202026-03-02%20at%2014.22.40%20%281%29.jpeg", caption: "Unterwegs in der Natur" },
  { id: 17, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/fl4en27d_WhatsApp%20Image%202026-03-02%20at%2014.22.40.jpeg", caption: "Tiere beobachten" },
  { id: 18, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/p1idwzsx_WhatsApp%20Image%202026-03-02%20at%2014.22.39.jpeg", caption: "Wandern auf dem Feldweg" },
  { id: 19, src: "https://customer-assets.emergentagent.com/job_natur-sunne-web/artifacts/dbl6vqje_WhatsApp%20Image%202026-03-02%20at%2014.24.55.jpeg", caption: "Gemeinsam unterwegs" }];


  return (
    <section
      id="galerie"
      className="py-20 px-4 section-surface"
      data-testid="galerie-section">

      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-[#D4A373] mb-4">
            <ImageIcon className="w-5 h-5" />
            <span className="font-semibold uppercase tracking-wider text-sm">Impressionen</span>
          </div>
          <h2
            className="font-handwritten text-4xl md:text-5xl lg:text-6xl font-bold text-[#4A6741]"
            data-testid="galerie-title">

            Galerie
          </h2>
          <p className="text-[#2C3E50] mt-4">
            Eindrücke aus unserer Waldspielgruppe
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {galleryImages.map((img, index) =>
          <div
            key={img.id}
            className={`polaroid ${index % 3 === 0 ? "rotate-[-2deg]" : index % 3 === 1 ? "rotate-[1deg]" : "rotate-[2deg]"}`}
            data-testid={`gallery-image-${img.id}`}>

              <div className="aspect-square w-full overflow-hidden rounded">
                <img
                  src={img.src}
                  alt={img.caption}
                  className="w-full h-full object-cover"
                  loading="lazy" 
                />
              </div>

              <p className="text-center text-[#2C3E50] mt-3 font-handwritten text-base md:text-lg">
                {img.caption}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Contact Form Section
const KontaktSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    plz_ort: "",
    child_name: "",
    child_birthdate: "",
    child_special_info: "",
    message: "",
    preferred_days: [],
    photo_consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const days = ["Montag", "Donnerstag"];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDayToggle = (day) => {
    setFormData((prev) => ({
      ...prev,
      preferred_days: prev.preferred_days.includes(day) ?
      prev.preferred_days.filter((d) => d !== day) :
      [...prev.preferred_days, day]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (!response.data?.success) {
        toast.error(FALLBACK_ERROR_MESSAGE);
      } else {
        toast.success(response.data.message);
        setFormData({
          name: "",
          email: "",
          phone: "",
          address: "",
          plz_ort: "",
          child_name: "",
          child_birthdate: "",
          child_special_info: "",
          message: "",
          preferred_days: [],
          photo_consent: false
        });
      }
    } catch (error) {
      toast.error(readErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      className="py-20 px-4 section-cream relative"
      data-testid="kontakt-section">

      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-[#D4A373] mb-4">
            <Mail className="w-5 h-5" />
            <span className="font-semibold uppercase tracking-wider text-sm">Kontakt & Anmeldung</span>
          </div>
          <h2
            className="font-handwritten text-4xl md:text-5xl lg:text-6xl font-bold text-[#4A6741] mb-4"
            data-testid="kontakt-title">

            Jetzt Anmelden
          </h2>
          <p className="text-[#2C3E50]">
            Melden Sie Ihr Kind für die Waldspielgruppe Sunnebergli an
          </p>
        </div>

        {/* Contact Info Cards - Above Form */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {/* Nathalie Schmid */}
          <div className="bg-white p-6 rounded-2xl soft-shadow border-2 border-[#E9EDC9]">
            <h3 className="font-handwritten text-2xl font-bold text-[#4A6741] mb-3">Nathalie Schmid</h3>
            <div className="space-y-2">
              <a href="tel:+41774156592" className="flex items-center gap-3 text-[#2C3E50] hover:text-[#4A6741] transition-colors">
                <Phone className="w-5 h-5 text-[#D4A373]" />
                077 415 65 92
              </a>
              <a href="mailto:schmidnathalie@bluewin.ch" className="flex items-center gap-3 text-[#2C3E50] hover:text-[#4A6741] transition-colors">
                <Mail className="w-5 h-5 text-[#D4A373]" />
                schmidnathalie@bluewin.ch
              </a>
            </div>
          </div>
          
          {/* Barbara Berger */}
          <div className="bg-white p-6 rounded-2xl soft-shadow border-2 border-[#E9EDC9]">
            <h3 className="font-handwritten text-2xl font-bold text-[#4A6741] mb-3">Barbara Berger</h3>
            <div className="space-y-2">
              <a href="tel:+41799304768" className="flex items-center gap-3 text-[#2C3E50] hover:text-[#4A6741] transition-colors">
                <Phone className="w-5 h-5 text-[#D4A373]" />
                079 930 47 68
              </a>
              <a href="mailto:babs.hofmann@bluewin.ch" className="flex items-center gap-3 text-[#2C3E50] hover:text-[#4A6741] transition-colors">
                <Mail className="w-5 h-5 text-[#D4A373]" />
                babs.hofmann@bluewin.ch
              </a>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white p-6 md:p-10 rounded-3xl soft-shadow border-2 border-[#E9EDC9]"
          data-testid="contact-form">

          <div className="grid md:grid-cols-2 gap-6">
            {/* Name */}
            <div>
              <label className="block text-[#4A6741] font-semibold mb-2" htmlFor="name">
                Ihr Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 form-input-organic"
                placeholder="Max Mustermann"
                data-testid="input-name" />

            </div>

            {/* Email */}
            <div>
              <label className="block text-[#4A6741] font-semibold mb-2" htmlFor="email">
                E-Mail *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 form-input-organic"
                placeholder="max@beispiel.ch"
                data-testid="input-email" />

            </div>

            {/* Phone */}
            <div>
              <label className="block text-[#4A6741] font-semibold mb-2" htmlFor="phone">
                Telefon *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 form-input-organic"
                placeholder="+41 79 123 45 67"
                data-testid="input-phone" />

            </div>

            {/* Address */}
            <div>
              <label className="block text-[#4A6741] font-semibold mb-2" htmlFor="address">
                Strasse & Nr. *
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 form-input-organic"
                placeholder="Musterstrasse 12"
                data-testid="input-address" />

            </div>

            {/* PLZ & Ort */}
            <div>
              <label className="block text-[#4A6741] font-semibold mb-2" htmlFor="plz_ort">
                PLZ & Ort *
              </label>
              <input
                type="text"
                id="plz_ort"
                name="plz_ort"
                value={formData.plz_ort}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 form-input-organic"
                placeholder="8352 Elsau"
                data-testid="input-plz-ort" />

            </div>

            {/* Child Name */}
            <div>
              <label className="block text-[#4A6741] font-semibold mb-2" htmlFor="child_name">
                Name des Kindes
              </label>
              <input
                type="text"
                id="child_name"
                name="child_name"
                value={formData.child_name}
                onChange={handleChange}
                className="w-full px-4 py-3 form-input-organic"
                placeholder="Anna"
                data-testid="input-child-name" />

            </div>

            {/* Child Birthdate */}
            <div className="md:col-span-2">
              <label className="block text-[#4A6741] font-semibold mb-2" htmlFor="child_birthdate">
                Geburtsdatum des Kindes
              </label>
              <input
                type="date"
                id="child_birthdate"
                name="child_birthdate"
                value={formData.child_birthdate}
                onChange={handleChange}
                className="w-full px-4 py-3 form-input-organic"
                data-testid="input-child-birthdate" />

            </div>

            {/* Child Special Info */}
            <div className="md:col-span-2">
              <label className="block text-[#4A6741] font-semibold mb-2" htmlFor="child_special_info">
                Besondere Informationen zum Kind
              </label>
              <textarea
                id="child_special_info"
                name="child_special_info"
                value={formData.child_special_info}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 form-input-organic resize-none"
                placeholder="Allergien, Unverträglichkeiten, Behinderungen, besondere Bedürfnisse, Medikamente usw."
                data-testid="input-child-special-info" />

            </div>

            {/* Preferred Days */}
            <div className="md:col-span-2">
              <label className="block text-[#4A6741] font-semibold mb-3">
                Bevorzugte Tage
              </label>
              <div className="flex flex-wrap gap-2">
                {days.map((day) =>
                <button
                  key={day}
                  type="button"
                  onClick={() => handleDayToggle(day)}
                  className={`px-4 py-2 rounded-full font-semibold transition-all ${
                  formData.preferred_days.includes(day) ?
                  "bg-[#4A6741] text-white" :
                  "bg-[#E9EDC9] text-[#4A6741] hover:bg-[#CCD5AE]"}`
                  }
                  data-testid={`day-${day.toLowerCase()}`}>

                    {day}
                  </button>
                )}
              </div>
            </div>

            {/* Message */}
            <div className="md:col-span-2">
              <label className="block text-[#4A6741] font-semibold mb-2" htmlFor="message">
                Nachricht
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-3 form-input-organic resize-none"
                placeholder="Ihre Nachricht oder Fragen an uns..."
                data-testid="input-message" />

            </div>

            {/* Photo Consent */}
            <div className="md:col-span-2">
              <div className="bg-[#FEFAE0] p-5 rounded-xl border-2 border-[#E9EDC9]">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="photo_consent"
                    checked={formData.photo_consent}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 rounded border-2 border-[#4A6741] text-[#4A6741] focus:ring-[#4A6741]"
                    data-testid="input-photo-consent" />

                  <span className="text-[#2C3E50] text-sm leading-relaxed">
                    <strong className="text-[#4A6741]">Einverständnis Fotos:</strong> Ich bin damit einverstanden, 
                    dass Fotos meines Kindes für die Webseite der Waldspielgruppe Sunnebergli verwendet werden dürfen. 
                    Die Fotos werden ausschliesslich für die Darstellung der Aktivitäten auf der Webseite genutzt.
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8 text-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-10 py-4 bg-[#4A6741] text-white font-bold text-lg btn-organic hover:bg-[#3A5230] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              data-testid="submit-button">

              {isSubmitting ? "Wird gesendet..." : "Anmeldung absenden"}
            </button>
          </div>
        </form>

        {/* Contact Info */}
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="flex items-center gap-4 bg-white p-6 rounded-2xl soft-shadow">
            <div className="w-12 h-12 rounded-full bg-[#E9EDC9] flex items-center justify-center text-[#4A6741]">
              <MapPin className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-[#4A6741]">Standort</h3>
              <p className="text-[#2C3E50]">Elsau, Schweiz</p>
            </div>
          </div>
          <div className="flex items-center gap-4 bg-white p-6 rounded-2xl soft-shadow">
            <div className="w-12 h-12 rounded-full bg-[#E9EDC9] flex items-center justify-center text-[#4A6741]">
              <Phone className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-[#4A6741]">Kontakt</h3>
              <p className="text-[#2C3E50]">Per Formular oder E-Mail</p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// Footer
const Footer = () =>
<footer className="bg-[#4A6741] text-white py-12 px-4" data-testid="footer">
    <div className="max-w-6xl mx-auto">
      <div className="grid md:grid-cols-3 gap-8 mb-8">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <img
          src="https://static.wixstatic.com/media/0c04d6_85ffbb0ed57444348824e28d78023ef4~mv2.gif"
          alt="Waldspielgruppe Sunnebergli Logo"
          className="w-14 h-14" />

          <div>
            <span className="font-handwritten text-2xl font-bold">Sunnebergli</span>
            <p className="text-sm opacity-80">Waldspielgruppe Elsau</p>
          </div>
        </div>

        {/* Contact Barbara */}
        <div>
          <h4 className="font-handwritten text-xl font-bold mb-3 text-[#FFD700]">Barbara Berger</h4>
          <div className="space-y-2 text-sm">
            <a href="tel:+41799304768" className="flex items-center gap-2 hover:text-[#FFD700] transition-colors">
              <Phone className="w-4 h-4" />
              079 930 47 68
            </a>
            <a href="mailto:babs.hofmann@bluewin.ch" className="flex items-center gap-2 hover:text-[#FFD700] transition-colors">
              <Mail className="w-4 h-4" />
              babs.hofmann@bluewin.ch
            </a>
          </div>
        </div>

        {/* Contact Nathalie */}
        <div>
          <h4 className="font-handwritten text-xl font-bold mb-3 text-[#FFD700]">Nathalie Schmid</h4>
          <div className="space-y-2 text-sm">
            <a href="tel:+41774156592" className="flex items-center gap-2 hover:text-[#FFD700] transition-colors">
              <Phone className="w-4 h-4" />
              077 415 65 92
            </a>
            <a href="mailto:schmidnathalie@bluewin.ch" className="flex items-center gap-2 hover:text-[#FFD700] transition-colors">
              <Mail className="w-4 h-4" />
              schmidnathalie@bluewin.ch
            </a>
          </div>
        </div>
      </div>

      {/* Links */}
      <div className="flex flex-wrap justify-center gap-6 text-sm py-6 border-t border-white/20">
        <a href="#home" className="hover:text-[#FFD700] transition-colors">Home</a>
        <a href="#philosophie" className="hover:text-[#FFD700] transition-colors">Philosophie</a>
        <a href="#team" className="hover:text-[#FFD700] transition-colors">Team</a>
        <a href="#angebot" className="hover:text-[#FFD700] transition-colors">Angebot</a>
        <a href="#infos" className="hover:text-[#FFD700] transition-colors">Infos</a>
        <a href="#kleidung" className="hover:text-[#FFD700] transition-colors">Kleidung</a>
        <a href="#galerie" className="hover:text-[#FFD700] transition-colors">Galerie</a>
        <a href="#kontakt" className="hover:text-[#FFD700] transition-colors">Kontakt</a>
      </div>

      <div className="pt-6 border-t border-white/20 text-center text-sm opacity-80">
        <p>© {new Date().getFullYear()} Waldspielgruppe Sunnebergli, Elsau. Alle Rechte vorbehalten.</p>
      </div>
    </div>
  </footer>;


// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: '#FEFAE0',
            color: '#2C3E50',
            border: '2px solid #4A6741'
          }
        }} />

      <Navigation />
      <main>
        <HeroSection />
        <PhilosophieSection />
        <TeamSection />
        <AngebotSection />
        <InfosSection />
        <KleidungSection />
        <GalerieSection />
        <KontaktSection />
      </main>
      <Footer />
    </div>);

}

export default App;
